import { memo, useCallback, useRef, useState } from 'react'
import L from 'leaflet'
import { Marker, Popup } from 'react-leaflet'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../context/AuthContext'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { listingAPI } from '../../services/api/listingAPI'
import { formatRelativeTime } from '../../utils/dateFormatting'
import ListingRequestModal from '../listings/ListingRequestModal'
import { toast } from '../../services/toastService'

// All category colors derived from or complementary to the app's 3-color palette:
// Primary #0ABFBC (teal), Accent #E07B39 (terracotta), Secondary #F7F3EE (ivory)
const CATEGORY_COLORS = {
  food:       '#E07B39', // app accent — terracotta, warm/food
  goods:      '#6366F1', // indigo — neutral/distinct
  service:    '#0ABFBC', // app primary — teal, professional
  mutual_aid: '#F59E0B', // amber — warm community/sharing
}

const CATEGORY_ICONS = {
  food: '🥘', goods: '📦', service: '🔧', mutual_aid: '🤝',
}

function buildListingIcon(category) {
  const bg = CATEGORY_COLORS[category] || CATEGORY_COLORS.goods
  const emoji = CATEGORY_ICONS[category] || '📌'
  return L.divIcon({
    html: `<div style="background:${bg};color:white;width:32px;height:32px;border-radius:50%;border:2.5px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;font-size:15px;line-height:1;">${emoji}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -36],
    className: 'listing-marker',
  })
}

/**
 * Popup content rendered inside the Leaflet popup.
 * Visual language matches entity popups: colored header strip, structured body, action row.
 */
const ListingPopupContent = memo(function ListingPopupContent({ listing, onFulfilled, onDeleted }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { execute, loading } = useAsyncOperation()

  const isOwner = !!(user && user.id === listing.user_id)
  const isExpired = new Date(listing.expires_at) < new Date()
  const bg = CATEGORY_COLORS[listing.category] || CATEGORY_COLORS.goods
  const emoji = CATEGORY_ICONS[listing.category] || '📌'
  const categoryLabel = t(`listings.category.${listing.category}`, listing.category)

  // Minutes until expiry for urgency hint
  const minsLeft = Math.round((new Date(listing.expires_at) - new Date()) / 60000)
  const expiryHint = isExpired
    ? t('listings.expired', 'Expired')
    : minsLeft < 60
      ? `${minsLeft}m left`
      : minsLeft < 1440
        ? `${Math.round(minsLeft / 60)}h left`
        : null

  const handleFulfill = useCallback(async (e) => {
    e?.stopPropagation()
    await execute(() => listingAPI.fulfillListing(listing.id), {
      onSuccess: () => {
        toast.success(t('listings.markedFulfilled', 'Marked as fulfilled'))
        onFulfilled?.(listing.id)
      },
      onError: () => toast.error(t('listings.fulfillFailed', 'Failed to update')),
    })
  }, [listing.id, execute, t, onFulfilled])

  const handleDelete = useCallback(async (e) => {
    e?.stopPropagation()
    await execute(() => listingAPI.deleteListing(listing.id), {
      onSuccess: () => {
        toast.success(t('listings.deleted', 'Listing removed'))
        onDeleted?.(listing.id)
      },
      onError: () => toast.error(t('listings.deleteFailed', 'Failed to delete')),
    })
  }, [listing.id, execute, t, onDeleted])

  return (
    <div className="w-full rounded-xl overflow-hidden">
      {/* Coloured header strip — category identity */}
      <div
        className="relative h-14 flex items-center justify-between px-3"
        style={{ background: `linear-gradient(135deg, ${bg}ee, ${bg}aa)` }}
      >
        <div className="flex items-center gap-2">
          <span className="text-2xl leading-none">{emoji}</span>
          <span className="text-white text-xs font-semibold uppercase tracking-wide opacity-90">
            {categoryLabel}
          </span>
        </div>
        {(expiryHint) && (
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
            isExpired
              ? 'bg-red-500/80 text-white'
              : minsLeft < 60
                ? 'bg-white/30 text-white'
                : 'bg-white/20 text-white/90'
          }`}>
            {expiryHint}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="px-3 pt-2.5 pb-3 space-y-2">
        {/* Title */}
        <p className="text-sm font-semibold text-gray-900 leading-snug break-words pr-2">
          {listing.title}
        </p>

        {/* Description */}
        {listing.description && (
          <p className="text-xs text-gray-500 leading-snug line-clamp-2">
            {listing.description}
          </p>
        )}

        {/* Price + quantity row */}
        <div className="flex items-center gap-2">
          {listing.price != null ? (
            <span className="text-base font-bold text-primary">
              ${Number(listing.price).toFixed(2)}
            </span>
          ) : (
            <span className="text-sm font-semibold text-emerald-600">
              {t('listings.free', 'Free')}
            </span>
          )}
          {listing.quantity && (
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
              {listing.quantity}
            </span>
          )}
        </div>

        {/* Address */}
        {listing.address && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <svg className="w-3 h-3 flex-shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="truncate">{listing.address}</span>
          </div>
        )}

        {/* Divider + meta */}
        <div className="flex items-center justify-between pt-0.5 border-t border-gray-100 text-[11px] text-gray-400">
          <span>
            <span className="font-medium text-gray-600">{listing.display_name}</span>
          </span>
          <span>{formatRelativeTime(listing.created_at)}</span>
        </div>

        {/* Owner actions */}
        {isOwner && !isExpired && (
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleFulfill}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold text-white transition-colors disabled:opacity-50"
              style={{ background: bg }}
            >
              <CheckCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
              {t('listings.markFulfilled', 'Mark fulfilled')}
            </button>
            <button
              onClick={handleDelete}
              disabled={loading}
              className="flex items-center justify-center w-8 rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              <TrashIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Contact hint for non-owners */}
        {!isOwner && (
          <div className="flex gap-2 pt-0.5">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${listing.latitude},${listing.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold text-white transition-opacity hover:opacity-90"
              style={{ background: 'rgb(var(--color-primary))' }}
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              {t('map.getDirections', 'Get Directions')}
            </a>
            <button
              onClick={(e) => {
                e.stopPropagation()
                const text = `${listing.title}${listing.price != null ? ` — $${Number(listing.price).toFixed(2)}` : ' (free)'}${listing.address ? `\n📍 ${listing.address}` : ''}\nPosted by ${listing.display_name} on TrckIt`
                navigator.clipboard?.writeText(text).then(() => toast.success(t('share.copied', 'Copied!')))
              }}
              className="flex items-center justify-center w-9 rounded-lg border border-gray-200 text-gray-500 hover:text-primary hover:border-primary/30 hover:bg-primary/5 transition-colors"
              title={t('share.link', 'Copy details')}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  )
})

/**
 * ListingMarker - ephemeral listing on the map with an inline Leaflet popup.
 * Participates in the same popup coordination as entity markers:
 * opening a listing popup closes any open entity popup and vice-versa.
 *
 * @param {Object}   listing              - listing data from API
 * @param {Function} onFulfilled          - called with listing.id after fulfillment
 * @param {Function} onDeleted            - called with listing.id after deletion
 * @param {Object}   currentOpenPopupRef  - shared ref tracking the map's open popup
 * @idempotent @atomic
 */
const ListingMarker = memo(function ListingMarker({ listing, onFulfilled, onDeleted, currentOpenPopupRef }) {
  const markerRef = useRef(null)
  const icon = buildListingIcon(listing.category)

  // Mirrors the coordination pattern in NormalModeMarker:
  // 1. Close whatever popup was open before (entity or listing)
  // 2. Open this popup
  // 3. Register this marker as the new open popup
  const handleClick = useCallback(() => {
    if (currentOpenPopupRef?.current && currentOpenPopupRef.current !== markerRef.current) {
      currentOpenPopupRef.current.closePopup()
    }
    if (markerRef.current) {
      markerRef.current.openPopup()
      if (currentOpenPopupRef) currentOpenPopupRef.current = markerRef.current
    }
  }, [currentOpenPopupRef])

  return (
    <Marker
      ref={markerRef}
      position={[listing.latitude, listing.longitude]}
      icon={icon}
      eventHandlers={{ click: handleClick }}
    >
      <Popup
        className="custom-popup listing-popup"
        autoClose={false}
        closeOnClick={false}
        autoOpen={false}
        autoPan={true}
        maxWidth={280}
        minWidth={220}
      >
        <ListingPopupContent
          listing={listing}
          onFulfilled={onFulfilled}
          onDeleted={onDeleted}
        />
      </Popup>
    </Marker>
  )
})

export default ListingMarker

